/**
 * Implementation guide shown on the right of the Legal Awareness zone &
 * woreda pages. Lists the steps with titles + descriptions and tracks which
 * steps are done vs. remaining based on the current record.
 */
export default function ZoneImplementationGuide({ variant, data }: { variant: "zone" | "woreda"; data?: any }) {
    const isZone = variant === "zone";

    const woredas = data?.distrbuted_to_woreda || data?.distributed_to_woreda || [];
    const hasFile = (data?.documents || []).some((d: any) =>
        ["distrbuted_zone_awarness", "distrbuted_tower_awarness", "legal_awareness_document_file"].includes(d?.document_type)
    );
    const distributed = woredas.length > 0;
    const hasReport = !!data?.report_summary;

    const steps = isZone
        ? [
            {
                title: "Review the awareness file",
                detail: "Review the legal awareness file sent from the region.",
                done: hasFile || distributed || hasReport,
            },
            {
                title: "Distribute to woredas",
                detail: "Use ‘Distribute to Woreda’ to forward it to the woredas in your zone.",
                done: distributed,
            },
            {
                title: "Submit zonal report",
                detail: "Track each woreda’s report below, then upload your consolidated zonal report.",
                done: hasReport,
            },
        ]
        : [
            {
                title: "Review the awareness file",
                detail: "Open the request and review the legal awareness file.",
                done: hasFile || hasReport,
            },
            {
                title: "Conduct the activity",
                detail: "Carry out the awareness activity in your woreda.",
                done: hasReport,
            },
            {
                title: "Submit your report",
                detail: "Use ‘Upload Report’ to submit your summary and report file.",
                done: hasReport,
            },
        ];

    const completed = steps.filter((s) => s.done).length;
    const total = steps.length;
    const currentIndex = steps.findIndex((s) => !s.done); // first remaining step
    const pct = Math.round((completed / total) * 100);

    return (
        <div className="w-[32%] flex flex-col gap-4 overflow-y-scroll scrollbar-hide">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                    <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">OAG</p>
                    <p className="text-white font-semibold text-base">Implementation Guide</p>
                    {/* Progress */}
                    <div className="mt-3">
                        <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[11px] text-white/80">{completed} of {total} steps completed</span>
                            <span className="text-[11px] font-semibold text-amber-300">{pct}%</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full rounded-full bg-amber-300 transition-all duration-500" style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <ol className="p-5 space-y-5">
                    {steps.map((step, idx) => {
                        const isCurrent = idx === currentIndex;
                        return (
                            <li key={idx} className="flex gap-3">
                                {/* status indicator */}
                                {step.done ? (
                                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </span>
                                ) : (
                                    <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isCurrent ? "bg-[#0A1D39] text-white" : "bg-gray-100 text-gray-400"}`}>
                                        {idx + 1}
                                    </span>
                                )}
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className={`text-sm font-semibold ${step.done ? "text-gray-700" : isCurrent ? "text-[#0A1D39]" : "text-gray-500"}`}>{step.title}</p>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${step.done ? "bg-green-100 text-green-700" : isCurrent ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                                            {step.done ? "Done" : isCurrent ? "In progress" : "Pending"}
                                        </span>
                                    </div>
                                    <p className="text-[13px] text-gray-500 leading-relaxed mt-0.5">{step.detail}</p>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}
